/* SpawnAnything — multiplayer wire protocol (shared by browser client and Node relay).
   Messages are plain objects with a string `type`. Encoding is JSON for now; everything goes
   through `encode`/`decode` so a binary codec can be swapped in later via `setCodec`. */

export const PROTOCOL_VERSION = 1;

/* Client -> server */
export const C2S = Object.freeze({
  CREATE: 'create',   // { name, rid? }
  JOIN: 'join',       // { code, name, rid? }
  RESUME: 'resume',   // { code, token, lastTurn, rid? }
  LEAVE: 'leave',     // {}
  TEAM: 'team',       // { team: 0|1 }
  READY: 'ready',     // { ready: bool }
  START: 'start',     // { seed: uint32, setup: JSON }   (host only)
  CMDS: 'cmds',       // { turn, cmds: [object…] }
  HASH: 'hash',       // { turn, hash: uint32 }
  PING: 'ping',       // { t, rtt? }
  REPLAY: 'replay',   // { from }  ask for missing turns
});

/* Server -> client */
export const S2C = Object.freeze({
  WELCOME: 'welcome', // { code, playerId, token, rid? }
  RESUMED: 'resumed', // { code, playerId, token, state, nextTurn, start|null, rid? }
  LOBBY: 'lobby',     // { code, state, hostId, maxPlayers, players:[{id,name,team,ready,host,connected}] }
  START: 'start',     // { seed, setup, turnMs, ticksPerTurn, graceMs, players }
  TURN: 'turn',       // { turn, cmds:[{player, cmd}] }
  DESYNC: 'desync',   // { turn, hashes:{ [playerId]: hash } }
  PONG: 'pong',       // { t, maxRtt }
  ERROR: 'error',     // { code, message, rid? }
});

export const LIMITS = Object.freeze({
  NAME_MAX: 24,
  CODE_LEN: 5,
  CMDS_BYTES: 4096,        // serialized commands per player per turn
  CMDS_PER_TURN: 64,
  CMD_BYTES: 4000,         // one command
  MSG_BYTES: 8192,         // any message except `start`
  START_BYTES: 1_500_000,  // `start` carries the opaque setup (armies, painted positions …)
  TURN_MAX: 0x7fffffff,
  TURNS_AHEAD: 256,        // a client may not submit further than this beyond the open turn
});

// Room codes avoid I and O so they can be read aloud / typed without confusion.
export const CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
const CODE_RE = /^[A-HJ-NP-Z]{5}$/;
const TOKEN_RE = /^[0-9a-f]{32}$/;

/* Error codes with German user-facing texts. */
export const ERRORS = Object.freeze({
  bad_message: 'Ungültige Nachricht.',
  too_large: 'Nachricht zu groß.',
  rate: 'Zu viele Nachrichten – bitte langsamer.',
  bad_name: 'Bitte gib einen Namen mit 1 bis 24 Zeichen ein.',
  not_found: 'Raum nicht gefunden. Prüfe den Code.',
  full: 'Der Raum ist voll.',
  started: 'Das Spiel in diesem Raum läuft bereits.',
  server_full: 'Der Server ist ausgelastet. Versuche es später erneut.',
  already_in_room: 'Du bist bereits in einem Raum.',
  not_in_room: 'Du bist in keinem Raum.',
  not_host: 'Nur der Gastgeber kann das Spiel starten.',
  not_ready: 'Noch nicht alle Spieler sind bereit.',
  not_lobby: 'Das geht nur in der Lobby.',
  not_running: 'Es läuft gerade kein Spiel.',
  expired: 'Deine Sitzung ist abgelaufen.',
  turn_range: 'Zug außerhalb des erlaubten Bereichs.',
  cmds_too_large: 'Zu viele oder zu große Befehle in einem Zug.',
  replay_unavailable: 'Verpasste Züge sind nicht mehr verfügbar.',
  timeout: 'Zeitüberschreitung bei der Verbindung zum Server.',
  connection: 'Verbindung zum Server fehlgeschlagen.',
  closed: 'Die Verbindung wurde getrennt.',
  version: 'Client und Server passen nicht zusammen. Bitte Seite neu laden.',
});
export const errorText = code => ERRORS[code] || ERRORS.bad_message;

/* ---------------- codec ---------------- */

const te = typeof TextEncoder !== 'undefined' ? new TextEncoder() : null;
const td = typeof TextDecoder !== 'undefined' ? new TextDecoder('utf-8', { fatal: false }) : null;

/** UTF-8 byte length of a JS string without allocating. */
export function utf8Length(s) {
  let n = 0;
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    if (c < 0x80) n += 1;
    else if (c < 0x800) n += 2;
    else if (c >= 0xd800 && c <= 0xdbff && i + 1 < s.length) { n += 4; i++; }
    else n += 3;
  }
  return n;
}

/** Raw size in bytes of an incoming frame (string, Buffer, ArrayBuffer, typed array or Buffer[]). */
export function rawSize(data) {
  if (typeof data === 'string') return utf8Length(data);
  if (data == null) return 0;
  if (Array.isArray(data)) { let n = 0; for (const d of data) n += rawSize(d); return n; }
  if (typeof data.byteLength === 'number') return data.byteLength;
  return 0;
}

function toText(data) {
  if (typeof data === 'string') return data;
  if (Array.isArray(data)) data = concatBytes(data);
  if (data instanceof ArrayBuffer) data = new Uint8Array(data);
  if (ArrayBuffer.isView(data)) {
    if (td) return td.decode(data);
    if (typeof data.toString === 'function') return data.toString('utf8');
  }
  return null;
}
function concatBytes(list) {
  let n = 0; for (const b of list) n += b.byteLength;
  const out = new Uint8Array(n); let o = 0;
  for (const b of list) { out.set(new Uint8Array(b.buffer, b.byteOffset, b.byteLength), o); o += b.byteLength; }
  return out;
}

const jsonCodec = Object.freeze({
  name: 'json',
  encode: msg => JSON.stringify(msg),
  decode: data => {
    const s = toText(data);
    if (s == null) return undefined;
    return JSON.parse(s);
  },
});

let codec = jsonCodec;
/** Swap the wire codec (must provide encode(msg) -> string|Uint8Array and decode(data) -> object). */
export function setCodec(c) { codec = c || jsonCodec; }
export function getCodec() { return codec; }

export function encode(msg) { return codec.encode(msg); }

/** Decode an incoming frame. Never throws; returns null for anything that is not `{ type: string, … }`. */
export function decode(data) {
  let m;
  try { m = codec.decode(data); } catch { return null; }
  if (!isPlainObject(m) || typeof m.type !== 'string' || m.type.length > 16) return null;
  return m;
}

/* ---------------- validation helpers ---------------- */

export function isPlainObject(v) {
  if (v === null || typeof v !== 'object' || Array.isArray(v)) return false;
  const p = Object.getPrototypeOf(v);
  return p === Object.prototype || p === null;
}
export const isInt = (v, lo = -Infinity, hi = Infinity) => Number.isInteger(v) && v >= lo && v <= hi;
export const isUint32 = v => isInt(v, 0, 0xffffffff);
export const isTurn = v => isInt(v, 0, LIMITS.TURN_MAX);
const isFiniteNum = v => typeof v === 'number' && Number.isFinite(v);
const optRid = m => m.rid === undefined || isInt(m.rid, 0, 0x7fffffff);

/** Normalize a player name: trims, collapses whitespace, strips control characters. Returns null if invalid. */
export function cleanName(name) {
  if (typeof name !== 'string' || name.length > 200) return null;
  const s = name.replace(/[\u0000-\u001f\u007f-\u009f\u200b-\u200f\u2028-\u202e]/g, '').replace(/\s+/g, ' ').trim();
  if (!s || [...s].length > LIMITS.NAME_MAX) return null;
  return s;
}
export function cleanCode(code) {
  if (typeof code !== 'string') return null;
  const c = code.trim().toUpperCase();
  return CODE_RE.test(c) ? c : null;
}
export const isToken = t => typeof t === 'string' && TOKEN_RE.test(t);

/** Byte budget for a single message type (start is the only large one). */
export const maxBytesFor = type => type === C2S.START ? LIMITS.START_BYTES : (type === C2S.CMDS ? LIMITS.CMDS_BYTES + 256 : LIMITS.MSG_BYTES);

/** A command is an opaque, JSON-serializable plain object. Returns its UTF-8 size or -1. */
export function commandSize(cmd) {
  if (!isPlainObject(cmd)) return -1;
  let s;
  try { s = JSON.stringify(cmd); } catch { return -1; }
  return typeof s === 'string' ? utf8Length(s) : -1;
}

/**
 * Validate a decoded client->server message. Returns null when valid, otherwise an error code
 * (key of ERRORS). `bytes` is the raw frame size.
 */
export function validateClientMessage(m, bytes = 0) {
  if (!isPlainObject(m) || typeof m.type !== 'string') return 'bad_message';
  if (bytes > maxBytesFor(m.type)) return 'too_large';
  if (!optRid(m)) return 'bad_message';
  switch (m.type) {
    case C2S.CREATE:
      return cleanName(m.name) ? null : 'bad_name';
    case C2S.JOIN:
      if (!cleanCode(m.code)) return 'not_found';
      return cleanName(m.name) ? null : 'bad_name';
    case C2S.RESUME:
      if (!cleanCode(m.code) || !isToken(m.token)) return 'expired';
      return isInt(m.lastTurn, -1, LIMITS.TURN_MAX) ? null : 'bad_message';
    case C2S.LEAVE:
      return null;
    case C2S.TEAM:
      return m.team === 0 || m.team === 1 ? null : 'bad_message';
    case C2S.READY:
      return typeof m.ready === 'boolean' ? null : 'bad_message';
    case C2S.START:
      if (!isUint32(m.seed)) return 'bad_message';
      return m.setup === undefined || m.setup === null || typeof m.setup === 'object' ? null : 'bad_message';
    case C2S.CMDS: {
      if (!isTurn(m.turn) || !Array.isArray(m.cmds)) return 'bad_message';
      if (m.cmds.length > LIMITS.CMDS_PER_TURN) return 'cmds_too_large';
      for (const c of m.cmds) if (!isPlainObject(c)) return 'bad_message';
      return null;
    }
    case C2S.HASH:
      return isTurn(m.turn) && isUint32(m.hash) ? null : 'bad_message';
    case C2S.PING:
      if (!isFiniteNum(m.t)) return 'bad_message';
      return m.rtt === undefined || (isFiniteNum(m.rtt) && m.rtt >= 0 && m.rtt <= 60000) ? null : 'bad_message';
    case C2S.REPLAY:
      return isTurn(m.from) ? null : 'bad_message';
    default:
      return 'bad_message';
  }
}

/** Light sanity check for server->client messages (the server is trusted, but frames may be garbled). */
export function validateServerMessage(m) {
  if (!isPlainObject(m)) return false;
  switch (m.type) {
    case S2C.TURN: return isTurn(m.turn) && Array.isArray(m.cmds);
    case S2C.LOBBY: return typeof m.code === 'string' && Array.isArray(m.players);
    case S2C.START: return isUint32(m.seed) && isInt(m.ticksPerTurn, 1, 1000) && isFiniteNum(m.turnMs);
    case S2C.WELCOME: return typeof m.code === 'string' && isInt(m.playerId, 1) && isToken(m.token);
    case S2C.RESUMED: return typeof m.code === 'string' && isInt(m.playerId, 1) && isTurn(m.nextTurn);
    case S2C.DESYNC: return isTurn(m.turn) && isPlainObject(m.hashes);
    case S2C.PONG: return isFiniteNum(m.t);
    case S2C.ERROR: return typeof m.code === 'string';
    default: return false;
  }
}
